import {
    type LessonResourceDTO,
    type ResourceItemDTO,
    type ResourceHeaderDTO,
    type UnitDTO,
} from '@/src/ui';
import Constants from './Constants';

// Prevents ES from showing unless the selected grade is in the allow list
export const filterLanguageSetting = (grade: string, language: string): string => {
    if (Constants.ALLOWED_ES_GRADES.includes(grade)) {
        return language;
    }
    return 'EN';
};

export const filterSlidesData = (
    grade: string,
    unitNumber: number,
    slidesData: LessonResourceDTO[],
    language?: string
): LessonResourceDTO[] => {
    if (Constants.ALLOWED_ES_GRADES.includes(grade) && grade !== 'TK' && language === 'ES') {
        switch (grade) {
            case 'K':
                return [1, 2, 3, 4, 5, 6, 8, 9].includes(unitNumber) ? slidesData : [];
            case '1':
                return [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(unitNumber) ? slidesData : [];
            case '2':
                return [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(unitNumber) ? slidesData : [];
            case '3':
                return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? slidesData : [];
            case '4':
                return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? slidesData : [];
            case '5':
                return [1, 2, 3, 4, 5, 6, 7].includes(unitNumber) ? slidesData : [];
            case '6':
                return [1, 2, 3, 4, 5, 6, 8].includes(unitNumber) ? slidesData : [];
            default:
                // Remaining Spanish grades have slides through Unit 6.
                return [1, 2, 3, 4, 5, 6].includes(unitNumber) ? slidesData : [];
        }
    }

    switch (grade) {
        case 'K':
            return [1, 2, 3, 4, 5, 6, 8, 9].includes(unitNumber) ? slidesData : [];
        case '1':
            return [1, 2, 3, 4, 5, 6, 7, 8, 9, 11].includes(unitNumber) ? slidesData : [];
        case '2':
            return [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(unitNumber) ? slidesData : [];
        case '3':
            return [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(unitNumber) ? slidesData : [];
        case '4':
            return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? slidesData : [];
        case '5':
            return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? slidesData : [];
        case '6':
            return [1, 2, 3, 4, 5, 6, 7, 8, 10].includes(unitNumber) ? slidesData : [];
        case '6M':
            // All regular PDFs are available; editable slides are available for Units 1-7 and 9.
            return slidesData.filter(
                (item) => !item.text.includes('Editable') || [1, 2, 3, 4, 5, 6, 7, 9].includes(unitNumber)
            );
        case '7':
            // All regular PDFs are available; editable slides are available for Units 1-7 and 9.
            return slidesData.filter(
                (item) =>
                    !item.text.includes('Editable') || [1, 2, 3, 4, 5, 6, 7, 9].includes(unitNumber)
            );
        case '8':
        // Allow all
        // falls through
        default:
            return slidesData;
    }
};

export const filterCentersData = (
    grade: string,
    unitNumber: number,
    centersData: LessonResourceDTO[]
): LessonResourceDTO[] => {
    switch (grade) {
        case 'K':
            return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? centersData : [];
        case '1':
            return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? centersData : [];
        case '2':
            return [1, 2, 3, 4, 5, 6, 7, 8].includes(unitNumber) ? centersData : [];
        default:
            return [];
    }
};

export const filterUnitsData = (grade: string, unitsData: UnitDTO[]): UnitDTO[] => {
    let allowedUnits = [];
    // Filters lesson videos from units that are not ready yet
    switch (grade) {
        case 'K':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
            break;
        case '1':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
            break;
        case '2':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
            break;
        case '3':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
            break;
        case '4':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
            break;
        case '5':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
            break;
        case '6':
        case '6M':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '7':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '8':
            allowedUnits = ['1', '2', '3', '4', '5', '7', '8', '9', '11'];
            break;
        default:
            return unitsData;
    }

    // Remove lesson videos from units that are not allowed for the current grade
    const filteredUnitsData = unitsData.map((unit) => {
        if (!allowedUnits.includes(`${unit.unitNumber}`)) {
            unit.lessons = unit.lessons.map((lesson) => {
                if (lesson.lessonNumber === 99 || !lesson.subitems) return lesson;

                lesson.subitems = lesson.subitems.filter((subitem) => {
                    return subitem.type && subitem.type !== 'Lesson Video';
                });

                return lesson;
            });
        }
        return unit;
    });

    return filteredUnitsData;
};

export const filterResourcesData = (
    assessmentsData: ResourceHeaderDTO[],
    grade: string,
    language?: string
): ResourceHeaderDTO[] => {
    let newResourcesData = [...assessmentsData];
    function removeActivityResources() {
        return newResourcesData.map((item: ResourceHeaderDTO) => {
            if (item.key === 'BTBFBox') {
                const fluencyWorksheets: ResourceItemDTO | undefined = item.items?.find(
                    (subitem: ResourceItemDTO) => subitem.text === 'Fluency Worksheets'
                );
                if (fluencyWorksheets) {
                    fluencyWorksheets.subitems = fluencyWorksheets.subitems?.filter(
                        (subitem: ResourceItemDTO) => !subitem.text.includes('Online Practice')
                    );
                }
            }
            return item;
        });
    }

    newResourcesData = removeActivityResources();

    function removeExemplarBox() {
        return newResourcesData.filter((item: ResourceHeaderDTO) => item.key !== 'exemplarsBox');
    }

    newResourcesData = removeExemplarBox();

    // Spanish Resources
    if (language === 'ES') {
        // GK: Filters out Calendar Routines
        if (grade === 'K') {
            function removeSpanishCalendarRoutines() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    item.items = item.items?.filter(
                        (subitem: ResourceItemDTO) => subitem.key !== 'CalendarRoutines'
                    );
                    return item;
                });
            }

            newResourcesData = removeSpanishCalendarRoutines();
        }

        // GK: Filters out BTBF Lessons
        if (grade === 'K') {
            function removeSpanishBTBFLessons() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        item.items = item.items?.filter(
                            (subitem: ResourceItemDTO) => subitem.key !== 'BTBFLessons'
                        );
                    }
                    return item;
                });
            }

            newResourcesData = removeSpanishBTBFLessons();
        }

        // GK: Filters out BTBF Fluency Worksheets, but keeps allowed ones
        if (grade === 'K') {
            function removeSpanishBTBFFluencyWorksheets() {
                const allowedSpanishFluencyItems = ['Suma Hasta 5', 'Resta Hasta 5'];
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        const spanishFluencyWorksheets = item.items?.find(
                            (subitem: ResourceItemDTO) => subitem.key === 'BTBFFluencyWorksheets'
                        );
                        if (spanishFluencyWorksheets) {
                            spanishFluencyWorksheets.subitems =
                                spanishFluencyWorksheets.subitems?.filter(
                                    (subitem: ResourceItemDTO) =>
                                        allowedSpanishFluencyItems.includes(subitem.text)
                                );
                        }
                    }
                    return item;
                });
            }

            newResourcesData = removeSpanishBTBFFluencyWorksheets();
        }

        // G6: Filters out Activity Pages Keys
        if (grade === '6') {
            function removeG6ActivityPagesKeys() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'teacherEditionBox') {
                        item.items = item.items?.map((subitem: ResourceItemDTO) => {
                            if (subitem.key === 'TeacherEditionActivityResources') {
                                subitem.subitems = subitem.subitems?.filter(
                                    (s: ResourceItemDTO) => !s.link?.includes('Activity_Pages_Keys')
                                );
                            }
                            return subitem;
                        });
                    }
                    return item;
                });
            }

            newResourcesData = removeG6ActivityPagesKeys();
        }

        // G6: Filters out the unavailable BTBF Online Practice PDF
        if (grade === '6') {
            function removeG6BTBFOnlinePractice() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        const fluencyWorksheets = item.items?.find(
                            (subitem: ResourceItemDTO) => subitem.key === 'BTBFFluencyWorksheets'
                        );
                        if (fluencyWorksheets) {
                            fluencyWorksheets.subitems = fluencyWorksheets.subitems?.filter(
                                (subitem: ResourceItemDTO) =>
                                    !subitem.link?.includes('Online_Practice.pdf')
                            );
                        }
                    }
                    return item;
                });
            }

            newResourcesData = removeG6BTBFOnlinePractice();
        }

        // GK, G3, and G6: Keep only the Spanish Additional Resources that are ready
        if (grade === 'K' || grade === '3' || grade === '6') {
            function keepAvailableSpanishAdditionalResources() {
                const availableResourceKeys = [
                    'blacklineMasters',
                    'mathematicalPractices',
                    'additionalResourcesMiscellaneous',
                    'numberPractice',
                    'scopeAndSequence',
                    'additionalResourcesVocabulary',
                    'wordUpActivities',
                    'calendarMats',
                ];
                const unavailableResourceFiles = [
                    'Addition_to_20_Flashcards.pdf',
                    'Number_Lines_10_to_-10__vertical.pdf',
                    'Number_Practice_0_and_1.pdf',
                    'Protractors.pdf',
                ];

                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'additionalResourcesBox') {
                        item.items = item.items
                            ?.filter((subitem: ResourceItemDTO) =>
                                availableResourceKeys.includes(subitem.key ?? '')
                            )
                            .map((subitem: ResourceItemDTO) => {
                                if (
                                    subitem.key === 'blacklineMasters' ||
                                    subitem.key === 'numberPractice'
                                ) {
                                    subitem.subitems = subitem.subitems?.filter(
                                        (resourceItem: ResourceItemDTO) =>
                                            !unavailableResourceFiles.some((fileName) =>
                                                resourceItem.link?.includes(fileName)
                                            )
                                    );
                                }
                                return subitem;
                            });
                    }
                    return item;
                });
            }

            newResourcesData = keepAvailableSpanishAdditionalResources();
        }
    }

    return newResourcesData;
};

export const filterAssessmentsData = (
    assessmentsData: ResourceHeaderDTO[],
    grade: string,
    language?: string
): ResourceHeaderDTO[] => {
    let newAssessmentData = [...assessmentsData];
    function removeOnlineAssessments() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'onlineAssessmentsBox'
        );
    }
    function removeActivitiesByStandard() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'activitesByStandardBox'
        );
    }

    newAssessmentData = removeOnlineAssessments();
    newAssessmentData = removeActivitiesByStandard();

    // G6 Spanish Diagnostic Activities are available; Activity Sheets are not.
    if (language === 'ES' && grade === '6') {
        newAssessmentData = newAssessmentData.map((item: ResourceHeaderDTO) => {
            if (item.key === 'diagnosticAssessmentsBox') {
                item.items = item.items?.map((diagnostic: ResourceItemDTO) => {
                    diagnostic.subitems = diagnostic.subitems?.filter(
                        (subitem: ResourceItemDTO) =>
                            !subitem.link?.includes('_Activity_Sheets.pdf')
                    );
                    return diagnostic;
                });
            }
            return item;
        });
    }

    return newAssessmentData;
};
